import React, { lazy, Suspense, useEffect, useLayoutEffect, useState } from 'react'
import { Router } from 'react-router'
import { ToastContainer } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import * as Sentry from '@sentry/react'

import AppLoader from '../../../components/Loaders/AppLoader'
import AppCrashedInfo from './AppCrashedInfo'
import UnauthorizedHeader from '../Header/UnauthorizedHeader'
import { MobileDown, MobileUp } from '../../../components/hoc/ResponsiveRendering'
import BannersContainer from './BannersContainer'
import { UnauthorizedRoutes } from '../../../Routes'
import ErrorMessage from '../../../components/Form/ErrorMessage'

import history from '../../../history'

import { checkAuthStatus } from '../../../modules/actions/cognito'
import {
  getCurrentUserIDSelector,
  getCurrentUserIsLoadingSelector,
  getCurrentUserWasLoadedSelector
} from '../../../modules/selectors/app'
import { isAuthorizedSelector, isInitialAuthCheckingSelector } from '../../../modules/selectors/cognito'

// import graphs initial setting
import '../Graphs'

import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file
import 'react-toastify/dist/ReactToastify.css'
import 'react-phone-input-2/lib/high-res.css' // styles for phone number input
import '../../../styles/index.css'
import 'keen-slider/keen-slider.min.css'
import 'react-loading-skeleton/dist/skeleton.css'

import useMainStyles from '../../../styles'

// lazy load Authorized app AuthorizedApp component to reduce the initial bundle file size and reduce time to show
// first content
const AuthorizedApp = lazy(() => import('./AuthorizedApp/AuthorizedApp'))

function App() {
  const dispatch = useDispatch()
  const [isFramed, setIsFramed] = useState(false)

  const currentUserId = useSelector(getCurrentUserIDSelector)
  const currentUserWasLoaded = useSelector(getCurrentUserWasLoadedSelector)
  const isUserProfileLoading = useSelector(getCurrentUserIsLoadingSelector)
  const isAuthorized = useSelector(isAuthorizedSelector)
  const isInitialLoading = useSelector(isInitialAuthCheckingSelector)

  // main index.js file with JSS setup
  // this is invoked as StylesPovider and JSS setup should be invoked before(in main index.js)
  useMainStyles()

  const isAuthorizedLoading = isAuthorized && !isInitialLoading && !currentUserWasLoaded && isUserProfileLoading

  useEffect(() => {
    dispatch(checkAuthStatus())
  }, [dispatch])

  useEffect(() => {
    // Checking if the page is in an iframe: determine if the current window (window.self) is the top-level window (window.top).
    // If they are not equal, it means that the page is being displayed within an iframe.
    if (window.top !== window.self) {
      // If framed, set isFramed state to true
      setIsFramed(true)
    }
  }, [])

  if (isFramed) {
    // The application should utilise frame-busting code. Frame-busting code is JavaScript which attempts to block the page from loading within an iFrame in situations where the X-Frame-Options header has not been respected by the browser.

    return <ErrorMessage error="This content cannot be displayed within a frame." />
  }

  const CustomRouter = ({ history, ...props }) => {
    // this component allows to have ability using custom history as Router in v6 is not supporting that
    const [state, setState] = useState({
      action: history.action,
      location: history.location
    })

    useLayoutEffect(() => history.listen(setState), [history])

    return <Router {...props} location={state.location} navigationType={state.action} navigator={history} />
  }

  return (
    <Sentry.ErrorBoundary
      fallback={() => {
        // Represent the Fallback page when App is crashed:
        return (
          <>
            <UnauthorizedHeader logoUrl={'/'} />
            <AppCrashedInfo />
          </>
        )
      }}
      beforeCapture={scope => {
        // include userId for sentry issue
        scope.setTag('currentUserId', currentUserId)
      }}
    >
      <Suspense fallback={<AppLoader />}>
        <CustomRouter history={history}>
          {
            // doesn't render routes until not getting authorised from BE side
            isInitialLoading || isAuthorizedLoading ? (
              <AppLoader />
            ) : (
              <>
                <BannersContainer />
                {isAuthorized ? (
                  // AuthorizedRoutes are App
                  <AuthorizedApp />
                ) : (
                  // UnauthorizedRoutes are Site
                  <>
                    <UnauthorizedHeader />
                    {/* put Suspense component here to prevent css missing in Header during the route switch */}
                    <Suspense fallback={<AppLoader />}>
                      <UnauthorizedRoutes />
                    </Suspense>
                  </>
                )}
              </>
            )
          }
          <MobileUp>
            <ToastContainer position="top-right" autoClose={5000} closeOnClick pauseOnVisibilityChange pauseOnHover />
          </MobileUp>
          <MobileDown>
            <ToastContainer position="top-center" autoClose={5000} closeOnClick pauseOnVisibilityChange pauseOnHover />
          </MobileDown>
        </CustomRouter>
      </Suspense>
    </Sentry.ErrorBoundary>
  )
}

export default App
